import type { ApiResponse } from '@/types';

const API_BASE = '/api';

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE}${endpoint}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      const data = await response.json() as T & { error?: string };

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}`,
        };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Auth API
export const authApi = {
  login: (login: string, password: string) =>
    api.post<{ user: import('@/types').User }>('/login', { login, password }),
  
  logout: () => api.post('/logout'),
  
  me: () => api.get<{ user: import('@/types').User }>('/me'),
};

// Exercises API
export const exercisesApi = {
  list: (includeArchived = false) =>
    api.get<import('@/types').Exercise[]>(
      `/exercises${includeArchived ? '?include_archived=1' : ''}`
    ),
  
  get: (id: string) => api.get<import('@/types').Exercise>(`/exercises/${id}`),
  
  create: (data: {
    name: string;
    muscle_primary: string;
    muscle_secondary?: string;
    technique?: string;
  }) => api.post<import('@/types').Exercise>('/exercises', data),
  
  update: (
    id: string,
    data: Partial<{
      name: string;
      muscle_primary: string;
      muscle_secondary?: string;
      technique?: string;
      archived: boolean;
    }>
  ) => api.patch<import('@/types').Exercise>(`/exercises/${id}`, data),
  
  delete: (id: string) => api.delete(`/exercises/${id}`),
  
  previousStats: () => api.get<Record<string, {
    exercise_id: string;
    exercise_name: string;
    workout_date: string;
    sets_count: number;
    total_work: number;
    max_weight: number;
    total_reps: number;
  }>>('/exercises/previous-stats'),
};

// Workouts API
export const workoutsApi = {
  list: (params?: { limit?: number; offset?: number; date_from?: string; date_to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    const query = searchParams.toString();
    return api.get<import('@/types').PaginatedResponse<import('@/types').Workout>>(
      `/workouts${query ? `?${query}` : ''}`
    );
  },
  
  active: () =>
    api.get<{ workout: import('@/types').Workout | null }>('/workouts/active'),
  
  get: (id: string) =>
    api.get<import('@/types').WorkoutWithSets>(`/workouts/${id}`),
  
  create: (date: string) =>
    api.post<import('@/types').Workout>('/workouts', { date }),
  
  finish: (id: string) =>
    api.patch<import('@/types').Workout>(`/workouts/${id}/finish`),
  
  delete: (id: string) => api.delete(`/workouts/${id}`),
  
  // Sets
  addSet: (
    workoutId: string,
    data: { exercise_id: string; weight: number; reps: number }
  ) => api.post<import('@/types').Set>(`/workouts/${workoutId}/sets`, data),
  
  updateSet: (
    workoutId: string,
    setId: string,
    data: { weight?: number; reps?: number }
  ) => api.patch<import('@/types').Set>(`/workouts/${workoutId}/sets/${setId}`, data),
  
  deleteSet: (workoutId: string, setId: string) =>
    api.delete(`/workouts/${workoutId}/sets/${setId}`),
};

// Measurements API
export const measurementsApi = {
  list: (params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return api.get<import('@/types').PaginatedResponse<import('@/types').Measurement>>(
      `/measurements${query ? `?${query}` : ''}`
    );
  },
  
  get: (id: string) =>
    api.get<import('@/types').Measurement>(`/measurements/${id}`),
  
  create: (data: Omit<import('@/types').Measurement, 'id' | 'user_id' | 'created_at'>) =>
    api.post<import('@/types').Measurement>('/measurements', data),
  
  update: (id: string, data: Partial<import('@/types').Measurement>) =>
    api.patch<import('@/types').Measurement>(`/measurements/${id}`, data),
  
  delete: (id: string) => api.delete(`/measurements/${id}`),
};

// Analytics API
export const analyticsApi = {
  weekly: (params?: { weeks?: number; year?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.weeks) searchParams.set('weeks', String(params.weeks));
    if (params?.year) searchParams.set('year', String(params.year));
    const query = searchParams.toString();
    return api.get<import('@/types').WeeklyAnalytics[]>(
      `/analytics/weekly${query ? `?${query}` : ''}`
    );
  },
  
  weeklyLive: (params?: { weeks?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.weeks) searchParams.set('weeks', String(params.weeks));
    const query = searchParams.toString();
    return api.get<import('@/types').WeeklyAnalytics[]>(
      `/analytics/weekly-live${query ? `?${query}` : ''}`
    );
  },
  
  calendar: (params?: { months?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.months) searchParams.set('months', String(params.months));
    const query = searchParams.toString();
    return api.get<import('@/types').CalendarData>(
      `/analytics/calendar${query ? `?${query}` : ''}`
    );
  },
  
  records: () => api.get<import('@/types').PersonalRecord[]>('/analytics/records'),
  
  monthly: (params?: { months?: number; year?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.months) searchParams.set('months', String(params.months));
    if (params?.year) searchParams.set('year', String(params.year));
    const query = searchParams.toString();
    return api.get<import('@/types').MonthlyAnalytics[]>(
      `/analytics/monthly${query ? `?${query}` : ''}`
    );
  },
};

// Profile API
export const profileApi = {
  get: () => api.get<import('@/types').User>('/profile'),
  
  update: (data: { display_name?: string; avatar_base64?: string }) =>
    api.patch<import('@/types').User>('/profile', data),
};

// Nutrition API
export const nutritionApi = {
  // Water
  getWater: (date?: string) => {
    const query = date ? `?date=${date}` : '';
    return api.get<{ date: string; glasses: number; total_ml: number }>(`/nutrition/water${query}`);
  },
  
  updateWater: (glasses: number, date?: string) =>
    api.post<{ date: string; glasses: number; total_ml: number }>('/nutrition/water', { glasses, date }),
  
  // Meals
  getMeals: (date?: string) => {
    const query = date ? `?date=${date}` : '';
    return api.get<{
      date: string;
      meals: import('@/types').Meal[];
      totals: { calories: number; protein: number; fat: number; carbs: number };
    }>(`/nutrition/meals${query}`);
  },
  
  createMeal: (data: {
    date?: string;
    meal_type: string;
    name: string;
    calories?: number;
    protein?: number;
    fat?: number;
    carbs?: number;
    note?: string;
  }) => api.post<import('@/types').Meal>('/nutrition/meals', data),
  
  updateMeal: (id: string, data: Partial<{
    meal_type: string;
    name: string;
    calories: number | null;
    protein: number | null;
    fat: number | null;
    carbs: number | null;
    note: string | null;
  }>) => api.patch<import('@/types').Meal>(`/nutrition/meals/${id}`, data),
  
  deleteMeal: (id: string) => api.delete(`/nutrition/meals/${id}`),
};

// Admin API
export const adminApi = {
  listUsers: () => api.get<import('@/types').User[]>('/admin/users'),
  
  createUser: (data: { login: string; password: string; role: 'admin' | 'user' }) =>
    api.post<import('@/types').User>('/admin/users', data),
  
  updateUser: (
    id: string,
    data: Partial<{ login: string; password: string; role: 'admin' | 'user' }>
  ) => api.patch<import('@/types').User>(`/admin/users/${id}`, data),
  
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
};
